import React, { useEffect, useState } from "react"
import { CustomModal } from "@components"
import { 
  Button, 
  Checkbox, 
  Chip, 
  ModalBody, 
  ModalContent, 
  ModalFooter, 
  ModalHeader, 
  Table, 
  TableBody, 
  TableCell, 
  TableColumn, 
  TableHeader, 
  TableRow 
} from "@heroui/react"

import { meetingTypes } from "@config"
import { PropTypes } from "prop-types"

const GroupBookingSlotsSelectorModal = ({ 
  buttonCaption = "Continue", 
  checkedSlotIds = [],
  description, 
  handleClose, 
  handleOK, 
  isProcessing = false, 
  show, 
  slots, 
  title 
}) => {
  const [selectedSlotIds, setSelectedSlotIds] = useState(checkedSlotIds)
  const [isAllSelected, setIsAllSelected] = useState(false)

  useEffect(() => {
    if (checkedSlotIds.length === slots.length) {
      setIsAllSelected(true)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectSlot = (slotId) => {
    setSelectedSlotIds((prevState) => {
      const newSlotsSelected = prevState.includes(slotId) 
        ? prevState.filter((id) => id !== slotId)
        : [...prevState, slotId]
      
      setIsAllSelected(newSlotsSelected.length === slots.length)
      
      return newSlotsSelected
    })
  }

  const handleToggleAll = (isChecked) => {
    setIsAllSelected(isChecked)
    isChecked ? setSelectedSlotIds(slots.map((slot) => slot.id)) : setSelectedSlotIds([])
  }

  return (
    <CustomModal
      isOpen={show}
      size="3xl"
      handleClose={() => handleClose()}
      scrollBehavior="inside"
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">Select Appointments</ModalHeader>

            <ModalBody>
              <div className='mt-2 mb-3'>
                <div className="flex items-center justify-between mb-2">
                  <h1 className="text-lg font-semibold">{title}</h1>
                  <Chip color={isAllSelected ? "success" : "primary"} className="ml-2 font-semibold text-white">
                    {selectedSlotIds.length} / {slots.length}
                  </Chip>
                </div>
                
                <p className='text-md'>{description}</p>
              </div>

              <Table
                aria-label="Select the required slots"
                classNames={{
                  loadingWrapper: "bg-white opacity-40",
                  wrapper: "rounded shadow-none p-0 overflow-y-hidden",
                }}
                color="primary"
                isStriped
                isCompact
                selectedKeys={selectedSlotIds}
                onSelectionChange={handleSelectSlot}
              >
                <TableHeader>
                  <TableColumn>
                    <Checkbox onChange={(e) => handleToggleAll(e.target.checked)} isSelected={isAllSelected} />
                  </TableColumn>
                  <TableColumn>Starts</TableColumn>
                  <TableColumn>lesson</TableColumn>
                  <TableColumn>Student</TableColumn>
                  <TableColumn>Guardian</TableColumn>
                  <TableColumn>Type</TableColumn>
                </TableHeader>

                <TableBody>
                  {slots.map((slot) => {
                    const typeColor = meetingTypes[slot.meeting_type_code.toUpperCase()].badgeColor

                    return (
                      <TableRow key={slot.id}>
                        <TableCell>
                          <Checkbox isSelected={selectedSlotIds.includes(slot.id)} onChange={() => handleSelectSlot(slot.id)}/>
                        </TableCell>
                        <TableCell>{slot.starts_at.format("HH:mm")}</TableCell>
                        <TableCell>{slot.lesson}</TableCell>
                        <TableCell>{slot.student}</TableCell>
                        <TableCell>{slot.parent}</TableCell>
                        <TableCell>
                          <Chip classNames={{
                            base: `${typeColor} text-white`,
                          }}>
                            {slot.meeting_type}
                          </Chip>
                        </TableCell>
                      </TableRow>
                    )})}
                </TableBody>
              </Table>
            </ModalBody>

            <ModalFooter>
              <div className="flex justify-between w-full">
                <Button onPress={() => handleClose()} tabIndex={-1}>Cancel</Button>
                <Button color="secondary" onPress={() => handleOK(selectedSlotIds)} isLoading={isProcessing}>{buttonCaption}</Button>
              </div>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </CustomModal>
  )
}


GroupBookingSlotsSelectorModal.propTypes = {
  buttonCaption: PropTypes.string,
  checkedSlotIds: PropTypes.arrayOf(PropTypes.string),
  description: PropTypes.string,
  handleClose: PropTypes.func.isRequired,
  handleOK: PropTypes.func.isRequired,
  isProcessing: PropTypes.bool,
  slots: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      starts_at: PropTypes.object.isRequired,
      lesson: PropTypes.string.isRequired,
      student: PropTypes.string.isRequired,
      parent: PropTypes.string.isRequired,
      meeting_type: PropTypes.string.isRequired,
      meeting_type_code: PropTypes.string,
    }).isRequired
  ),
  show: PropTypes.bool,
  title: PropTypes.string.isRequired,
}

export default GroupBookingSlotsSelectorModal